// Choose a lock (S31 step 5, Tool Plan §4.14, D-77; I-1a Batch 4, IP2-29 (3)).
// Also used after opening with the recovery code (a new lock for next time).
//
// The kind is chosen first, so the 6-digit PIN's honest limit is read AT the
// choice. The secret is typed twice. Refusals come from lock/secretPolicy and
// are said calmly; nothing is stored here.

import { type FormEvent, useState } from 'react'

import { checkSecret, type SecretKind, type SecretRefusal } from '../lock'
import { ScreenTitle } from '../ui/ScreenTitle'
import { SecretField } from '../ui/SecretField'

import { common, setLock } from './copy'

interface SetLockProps {
  readonly title: string
  readonly intro?: string
  readonly submitLabel: string
  readonly busy: boolean
  /** A problem from the caller (e.g. saving failed). */
  readonly message: string | null
  readonly onChosen: (secret: string) => void
  readonly onBack?: () => void
}

const KINDS: readonly SecretKind[] = ['passphrase', 'digits', 'pin6']

function refusalText(refusal: SecretRefusal, kind: SecretKind): string {
  const text = setLock.refusals[refusal]
  return typeof text === 'string' ? text : text[kind]
}

export function SetLock({ title, intro, submitLabel, busy, message, onChosen, onBack }: SetLockProps): React.JSX.Element {
  const [kind, setKind] = useState<SecretKind>('passphrase')
  const [value, setValue] = useState('')
  const [again, setAgain] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  const choose = (next: SecretKind): void => {
    setKind(next)
    setValue('')
    setAgain('')
    setProblem(null)
  }

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    if (busy) {
      return
    }
    const check = checkSecret(value)
    if (!check.ok) {
      setProblem(refusalText(check.refusal, kind))
      return
    }
    if (check.kind !== kind) {
      setProblem(setLock.wrongKind)
      return
    }
    if (value !== again) {
      setAgain('')
      setProblem(setLock.mismatch)
      return
    }
    setProblem(null)
    onChosen(value)
  }

  const numeric = kind !== 'passphrase'
  const shown = problem ?? message

  return (
    <form className="screen__body" onSubmit={submit} noValidate>
      <ScreenTitle>{title}</ScreenTitle>
      {intro ? <p className="screen__text">{intro}</p> : null}
      <fieldset className="choice" disabled={busy}>
        <legend className="choice__legend">{setLock.kindLegend}</legend>
        {KINDS.map((k) => (
          <label key={k} className="choice__option">
            <input type="radio" name="lock-kind" value={k} checked={kind === k} onChange={() => choose(k)} />
            <span>{setLock.kinds[k]}</span>
          </label>
        ))}
      </fieldset>
      {kind === 'pin6' ? <p className="notice">{setLock.pinLimit}</p> : null}
      <SecretField id="new-secret" label={setLock.field[kind]} value={value} onChange={setValue} numeric={numeric} disabled={busy} />
      <SecretField id="new-secret-again" label={setLock.again} value={again} onChange={setAgain} numeric={numeric} disabled={busy} />
      {shown ? (
        <p className="screen__problem" role="alert">
          {shown}
        </p>
      ) : null}
      {busy ? (
        <p className="screen__status" role="status">
          {common.opening}
        </p>
      ) : null}
      <div className="actions">
        <button type="submit" className="button button--primary" disabled={busy}>
          {submitLabel}
        </button>
        {onBack ? (
          <button type="button" className="button button--quiet" onClick={onBack} disabled={busy}>
            {common.back}
          </button>
        ) : null}
      </div>
    </form>
  )
}
